import { DoodProductModel } from '@core/models/product.model';
import { IAdditionGroup } from '@core/models/shop.model';
import {
  AdditionGroupsValidity,
  DraftAdditionsGroup,
  ValidityWithAdditionsByGroupsState,
} from '@shared/local-store/cart/util/cart-simple-product-util';
import { SimpleProductWithAdditionsGroupStep } from '../stepper.type';
import {
  ProductWithUIData,
  SimpleProductCartValidity,
} from '@shared/local-store/cart/cart-draft-declinable-product.store';
import { SimpleProductConfigStepperData } from '../simple-product-stepper.store';
import { StepInfo } from '@core/models/stepper.model';
import { getAdditionGroupPosition } from './stepper-util';
import { AutoFillCartItemRulesAdditionsGroup } from '@shared/local-store/cart/auto-fill-cart-item-rules/auto-fill-cart-item-rules-additions-group';

export class SimpleProductCartUtil {
  static calculateSimpleProductStates({
    simpleProductAdditionsByGroups,
    selectedAdditionsGroupId,
    isAdditionGroupValid,
    totalGroupAdditionsQuantity,
    product,
    setSelectedStep,
    currentStep,
    allProducts,
    additionGroupsValidity,
    _debugMissingStepDetected,
  }: {
    simpleProductAdditionsByGroups: IAdditionGroup[];
    selectedAdditionsGroupId: string | undefined;
    isAdditionGroupValid: boolean;
    totalGroupAdditionsQuantity: number;
    product: DoodProductModel | undefined;
    setSelectedStep: (stepsId: StepInfo) => void;
    currentStep: StepInfo | undefined;
    allProducts: DoodProductModel[];
    additionGroupsValidity: AdditionGroupsValidity | undefined;
    /**
     *  only used to check if a user has a missing step (the selection may be loosed...)
     */
    _debugMissingStepDetected: (data: {
      product: DoodProductModel | undefined;
      selectedAdditionsGroupId: string | undefined;
      currentStepIndex: number | undefined;
      additionGroupsValidity: AdditionGroupsValidity | undefined;
    }) => void;
  }): SimpleProductWithAdditionsGroupStep | undefined {
    const additionGroupIndex = simpleProductAdditionsByGroups.findIndex(
      additionGroup => additionGroup.id === selectedAdditionsGroupId,
    );
    const additionGroup = simpleProductAdditionsByGroups[additionGroupIndex];
    if (!additionGroup) {
      return undefined;
    }
    const hasExclusiveAdditionInAdditionsGroup =
      AutoFillCartItemRulesAdditionsGroup.hasExclusiveAdditionInAdditionsGroup({
        additionGroup,
      });
    const skippable = hasExclusiveAdditionInAdditionsGroup;
    const hasMaxQuantity = additionGroup.max_count !== undefined;
    const isMaxCountReached = totalGroupAdditionsQuantity >= (additionGroup.max_count ?? Infinity);
    const canMakeMoreChoices = hasMaxQuantity ? !isMaxCountReached : false;

    // #DOOD-1026 Sometimes some categories (maybe also additionsGroup?) selections seems missing https://dood-team.atlassian.net/browse/DOOD-1026?focusedCommentId=12237
    // This will enable to show the additions groups with missing selection and allow the user to add the product in the cart
    const missingAdditionGroupsValidity = additionGroupsValidity
      ? Object.entries(additionGroupsValidity.additionsByGroupsValidity).filter(
          ([_, additionGroupValidity]) => additionGroupValidity.isInvalid,
        )
      : [];
    const hasMissingAdditions = missingAdditionGroupsValidity.length > 0;
    const firstMissingAdditionGroupId = missingAdditionGroupsValidity[0]?.[0];
    const firstMissingAdditionGroupPosition = getAdditionGroupPosition({
      product,
      allProducts,
      groupId: firstMissingAdditionGroupId ?? '',
      additionsGroups: simpleProductAdditionsByGroups,
    });

    const currentStepIndex = simpleProductAdditionsByGroups.indexOf(additionGroup);
    const hasNextStepIndex = currentStepIndex < simpleProductAdditionsByGroups.length - 1;
    const hasNextStep = hasNextStepIndex || hasMissingAdditions;
    const hasPreviousStep = currentStepIndex > 0;
    const previousStepId = simpleProductAdditionsByGroups[currentStepIndex - 1]?.id;
    const nextStepId =
      simpleProductAdditionsByGroups[currentStepIndex + 1]?.id || firstMissingAdditionGroupId;

    const additionGroupPosition = getAdditionGroupPosition({
      product,
      allProducts,
      groupId: selectedAdditionsGroupId ?? '',
      additionsGroups: simpleProductAdditionsByGroups,
    });
    if (hasMissingAdditions && firstMissingAdditionGroupPosition < additionGroupPosition) {
      console.log('_debugMissingStepDetected', _debugMissingStepDetected);

      _debugMissingStepDetected({
        currentStepIndex,
        additionGroupsValidity,
        product,
        selectedAdditionsGroupId,
      });
    }

    return {
      type: 'simpleProductWithAdditionsGroup',
      additionGroup,
      product,
      selectedAdditionsGroupId,
      selectedStepId: selectedAdditionsGroupId,
      isValid: isAdditionGroupValid,
      canMakeMoreChoices,
      minStepCount: additionGroup.min_count ?? 0,
      maxStepCount: additionGroup.max_count ?? Infinity,
      count: totalGroupAdditionsQuantity,
      hasNextStep,
      hasPreviousStep,
      previousStepId,
      nextStepId,
      skippable,
      additionsGroups: simpleProductAdditionsByGroups,
      additionGroupPosition,
      nextStep: hasNextStep
        ? {
            ...currentStep,
            additionsGroupId: nextStepId,
          }
        : undefined,
      previousStep: hasPreviousStep
        ? {
            ...currentStep,
            additionsGroupId: previousStepId,
          }
        : undefined,
      setSelectedStep,
    } satisfies SimpleProductWithAdditionsGroupStep;
  }

  static getAdditionGroupTotalSelected({
    simpleProductAdditionsByGroups,
    selectedAdditionsGroupId,
    selectedAdditionIds,
  }: {
    simpleProductAdditionsByGroups: IAdditionGroup[];
    selectedAdditionsGroupId: string | undefined;
    selectedAdditionIds: string[];
  }) {
    if (!selectedAdditionsGroupId) {
      return 0;
    }
    const selectedAdditionsGroup = simpleProductAdditionsByGroups.find(
      group => group.id === selectedAdditionsGroupId,
    );
    return selectedAdditionIds.filter(selectedAdditionId => {
      return selectedAdditionsGroup?.items.some(item => item.id === selectedAdditionId);
    }).length;
  }

  static isSelectedGroupValid({
    selectedAdditionsGroupId,
    cartValidity,
  }: {
    selectedAdditionsGroupId: string | undefined;
    cartValidity: ValidityWithAdditionsByGroupsState;
  }) {
    if (!selectedAdditionsGroupId) {
      return false;
    }

    return cartValidity.additionsByGroupsValidity[selectedAdditionsGroupId].isValid;
  }

  static getSimpleProductCartStepperData({
    selectedSimpleProductId,
    simpleProduct,
    cartValidity,
    additionGroups,
    selectedAdditionIds,
  }: {
    selectedSimpleProductId: string | undefined;
    simpleProduct: ProductWithUIData | undefined;
    cartValidity: SimpleProductCartValidity | undefined;
    additionGroups: DraftAdditionsGroup[] | undefined;
    selectedAdditionIds: string[] | undefined;
  }): SimpleProductConfigStepperData | undefined {
    const _selectedSimpleProductId = selectedSimpleProductId;
    if (!_selectedSimpleProductId) {
      return undefined;
    }
    if (!simpleProduct) {
      return undefined;
    }
    if (!cartValidity) {
      return undefined;
    }
    if (!selectedAdditionIds) {
      return undefined;
    }
    return {
      additionsByGroups: additionGroups ?? [],
      product: simpleProduct,
      selectedAdditionIds,
      cartValidity,
    } satisfies SimpleProductConfigStepperData;
  }
}
